// My numbers (MEMBER-ENTRY, founder-approved 2026-10-21): the pure half of the member's entry
// screen and its Home card — the status chip ("Late", "Due Fri", "Due today", "Entered"), the
// period labels of the picker by cadence, and whether a value is a correction (it then needs a
// reason, which the API enforces: 422 without one).
import type { OperationOutput } from "@companyos/sdk";
import { weekRangeLabel } from "../../focus/weeks";

export type MyNumbers = OperationOutput<"me.numbers">;
export type MySeat = MyNumbers["seats"][number];
export type MyNumberItem = MySeat["items"][number];

const DAY = 86_400_000;
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;
const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

const noon = (day: string) => new Date(`${day}T12:00:00Z`);

export type DueTone = "late" | "due" | "done";

/**
 * The row's chip: a late period is "Late"; a due one "Due today", "Due Fri" (within the coming
 * week) or "Due Oct 31"; an entered one "Entered". `today` is the viewer's local day.
 */
export function dueChip(
  item: Pick<MyNumberItem, "status" | "due_on">,
  today: string,
): { label: string; tone: DueTone } {
  if (item.status === "late") return { label: "Late", tone: "late" };
  if (item.status === "entered") return { label: "Entered", tone: "done" };
  const days = Math.round((noon(item.due_on).getTime() - noon(today).getTime()) / DAY);
  if (days < 0) return { label: "Late", tone: "late" };
  if (days === 0) return { label: "Due today", tone: "due" };
  const d = noon(item.due_on);
  if (days < 7) return { label: `Due ${WEEKDAYS[d.getUTCDay()]}`, tone: "due" };
  return { label: `Due ${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`, tone: "due" };
}

/** A period key as the picker shows it: "W42 · Oct 12 – 18", "Oct 2026", "Q4 2026", "Fri Oct 16". */
export function periodLabel(type: MyNumberItem["period_type"], key: string): string {
  switch (type) {
    case "week":
      return `W${key.slice(6)} · ${weekRangeLabel(key)}`;
    case "month": {
      const [y, m] = key.split("-");
      return `${MONTHS[Number(m) - 1] ?? m} ${y}`;
    }
    case "quarter": {
      const [y, q] = key.split("-");
      return `${q} ${y}`;
    }
    case "day": {
      const d = noon(key);
      return `${WEEKDAYS[d.getUTCDay()]} ${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`;
    }
    default:
      return key;
  }
}

/** The live value the picked period already has (null: none), from the item's recent periods. */
export function valueIn(item: Pick<MyNumberItem, "recent">, periodKey: string): number | null {
  return item.recent.find((p) => p.period_key === periodKey)?.value ?? null;
}

/** A new value for a period that has a different one is a correction: it needs a reason. */
export function isCorrection(
  item: Pick<MyNumberItem, "recent">,
  periodKey: string,
  next: number | null,
): boolean {
  const live = valueIn(item, periodKey);
  return live !== null && next !== null && live !== next;
}

/** The items still to enter (late first, then due), across every seat. */
export function outstanding(
  numbers: MyNumbers | undefined,
): { seat: MySeat; item: MyNumberItem }[] {
  const rows = (numbers?.seats ?? []).flatMap((seat) =>
    seat.items.filter((i) => i.status !== "entered").map((item) => ({ seat, item })),
  );
  return rows.sort(
    (a, b) =>
      Number(b.item.status === "late") - Number(a.item.status === "late") ||
      a.item.due_on.localeCompare(b.item.due_on) ||
      a.item.name.localeCompare(b.item.name),
  );
}
